// 1회용 callback registry — native bridge 가 `singleUse.<funcName>(param)` 형태로 호출.
//
// iframe 컨텍스트: native 가 글로벌 `window.singleUse[funcName]` 을 호출하므로
//   window 객체에 등록 필요. mailview.ts 와 ViewerIFace 양쪽이 동일 store 공유.
// non-iframe 컨텍스트 (현재 미존재, §8.5 Future Work): native 가 없으므로 글로벌 노출
//   불필요 — 모듈 내부 state 로 관리.
//
// 이 helper 가 두 곳의 중복 createSingleUseFunc 를 단일 owner 로 통합 (Phase 3 #2).

import { isIframeMode } from './iframeMode'

// 미응답 콜백(native 가 끝내 호출하지 않은 경우)은 클로저가 메일 DOM 을 붙잡아 누수가 된다.
// createSingleUseFunc 호출 시점에 TTL(120s) 경과 엔트리를 age-out 으로 회수한다.
const SINGLE_USE_TTL_MS = 120000

// wrapper 에 생성 시점을 기록해 sweep 대상 판단에 사용.
type SingleUseFn = ((param: unknown) => void) & { createdAt?: number }

interface SingleUseHost {
  singleUse?: Record<string, SingleUseFn>
}

// non-iframe 컨텍스트용 모듈 내부 state.
const localStore: Record<string, SingleUseFn> = {}

const isIframe = isIframeMode()

function getStore(): Record<string, SingleUseFn> {
  if (isIframe) {
    const host = window as SingleUseHost
    if (host.singleUse == null) {
      host.singleUse = {}
    }
    return host.singleUse
  }
  return localStore
}

/**
 * 1회용 콜백을 store 에 등록하고, native 가 호출할 때 사용할 식별자를 반환.
 * 식별자는 `'singleUse.<funcName>'` 형식 — native 측에서 `eval` / 동적 호출에 활용.
 *
 * @param cb store 에 한 번 호출되고 자동 삭제될 callback.
 * @returns native 가 호출할 식별자 (예: `'singleUse.func1700000000000'`).
 */
/**
 * TTL(120s) 경과한 미응답 콜백을 store 에서 회수.
 * native 가 끝내 호출하지 않은 콜백의 클로저가 메일 DOM 을 영구 보존하는 누수를 방지한다.
 */
function sweepSingleUse(): void {
  const store = getStore()
  const now = Date.now()
  Object.keys(store).forEach((name) => {
    const fn = store[name]
    if (fn != null && fn.createdAt != null && now - fn.createdAt > SINGLE_USE_TTL_MS) {
      delete store[name]
    }
  })
}

export function createSingleUseFunc(cb: SingleUseFn): string {
  const store = getStore()
  sweepSingleUse()

  const curTime = Date.now()
  let funcName = 'func' + curTime
  let idx = 0
  while (store[funcName] != null) {
    funcName = 'func' + curTime + '_' + idx
    idx += 1
  }
  const wrapper: SingleUseFn = (param) => {
    cb(param)
    delete store[funcName]
  }
  wrapper.createdAt = curTime
  store[funcName] = wrapper
  return 'singleUse.' + funcName
}

/**
 * `createSingleUseFunc` 로 등록된 callback 을 식별자(`'singleUse.<funcName>'`) 기반으로 호출.
 *
 * non-iframe (inner viewer) 컨텍스트에서는 store 가 모듈 내부 `localStore` 라
 * `window.singleUse.<funcName>` 패턴으로 접근 불가능 — helper 가 store 의
 * 진실의 소스(single source of truth) 를 알므로 helper 가 호출까지 책임진다.
 *
 * iframe 컨텍스트에서는 host (viewerExt) 의 callCBStr 가 viewer 의
 * `contentWindow.singleUse.<funcName>` 으로 직접 접근하므로 본 함수 사용 불필요 —
 * helper 가 host 측에서 호출되면 host 의 `localStore` (비어있음) 를 보게 되어 fail.
 *
 * @param cbStr `'singleUse.<funcName>'` 형식의 식별자
 * @param param callback 의 단일 인자 (현재 모든 호출 site 가 단일 string 또는 null 사용)
 */
export function callSingleUse(cbStr: string, param: unknown): void {
  const parts = cbStr.split('.')
  if (parts.length !== 2 || parts[0] !== 'singleUse') {
    return
  }
  const store = getStore()
  const fn = store[parts[1]]
  if (fn == null) {
    return
  }
  fn(param)
}
